import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Plus, Trash2, Zap } from 'lucide-react';
import { WorkflowStage } from '@/hooks/useWorkflows';

export interface WorkflowAutomation {
  id?: string;
  stage_id: string;
  action_type: 'send_email' | 'create_task' | 'send_notification' | 'webhook';
  action_config: any;
  is_active: boolean;
}

interface AutomationRulesEditorProps {
  stages: WorkflowStage[];
  automations: WorkflowAutomation[];
  onAutomationsChange: (automations: WorkflowAutomation[]) => void;
}

export const AutomationRulesEditor = ({
  stages,
  automations,
  onAutomationsChange,
}: AutomationRulesEditorProps) => {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  const addAutomation = () => {
    const newAutomation: WorkflowAutomation = {
      stage_id: stages[0]?.id || '',
      action_type: 'create_task',
      action_config: {},
      is_active: true,
    };
    onAutomationsChange([...automations, newAutomation]);
    setExpandedIndex(automations.length);
  };

  const updateAutomation = (index: number, updates: Partial<WorkflowAutomation>) => {
    const updated = [...automations];
    updated[index] = { ...updated[index], ...updates };
    onAutomationsChange(updated);
  };

  const removeAutomation = (index: number) => {
    const updated = automations.filter((_, i) => i !== index);
    onAutomationsChange(updated);
    if (expandedIndex === index) {
      setExpandedIndex(null);
    }
  };

  const renderActionConfig = (automation: WorkflowAutomation, index: number) => {
    const config = automation.action_config || {};

    switch (automation.action_type) {
      case 'create_task':
        return (
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs">Task Title</Label>
              <Input
                value={config.title || ''}
                onChange={(e) => updateAutomation(index, {
                  action_config: { ...config, title: e.target.value }
                })}
                placeholder="Follow up with customer"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Description</Label>
              <Textarea
                value={config.description || ''}
                onChange={(e) => updateAutomation(index, {
                  action_config: { ...config, description: e.target.value }
                })}
                placeholder="Task description..."
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Priority</Label>
                <Select
                  value={config.priority || 'medium'}
                  onValueChange={(value) => updateAutomation(index, {
                    action_config: { ...config, priority: value }
                  })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Due in (days)</Label>
                <Input
                  type="number"
                  value={config.due_date || ''}
                  onChange={(e) => updateAutomation(index, {
                    action_config: { ...config, due_date: parseInt(e.target.value) || 0 }
                  })}
                  placeholder="7"
                />
              </div>
            </div>
          </div>
        );

      case 'send_email':
        return (
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs">Email Subject</Label>
              <Input
                value={config.subject || ''}
                onChange={(e) => updateAutomation(index, {
                  action_config: { ...config, subject: e.target.value }
                })}
                placeholder="Status Update"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Email Body</Label>
              <Textarea
                value={config.body || ''}
                onChange={(e) => updateAutomation(index, {
                  action_config: { ...config, body: e.target.value }
                })}
                placeholder="Email content..."
                rows={4}
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Email will be sent to the job owner's email address
            </p>
          </div>
        );

      case 'send_notification':
        return (
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs">Notification Message</Label>
              <Textarea
                value={config.message || ''}
                onChange={(e) => updateAutomation(index, {
                  action_config: { ...config, message: e.target.value }
                })}
                placeholder="Notification message..."
                rows={3}
              />
            </div>
          </div>
        );

      case 'webhook':
        return (
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs">Webhook URL</Label>
              <Input
                value={config.url || ''}
                onChange={(e) => updateAutomation(index, {
                  action_config: { ...config, url: e.target.value }
                })}
                placeholder="https://api.example.com/webhook"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Method</Label>
              <Select
                value={config.method || 'POST'}
                onValueChange={(value) => updateAutomation(index, {
                  action_config: { ...config, method: value }
                })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="POST">POST</SelectItem>
                  <SelectItem value="PUT">PUT</SelectItem>
                  <SelectItem value="PATCH">PATCH</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label className="flex items-center gap-2">
          <Zap className="w-4 h-4 text-primary" />
          Automation Rules
        </Label>
        <Button onClick={addAutomation} size="sm" variant="outline">
          <Plus className="w-4 h-4 mr-2" />
          Add Rule
        </Button>
      </div>

      <div className="space-y-3">
        {automations.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="p-6 text-center text-sm text-muted-foreground">
              No automation rules configured. Click "Add Rule" to create one.
            </CardContent>
          </Card>
        ) : (
          automations.map((automation, index) => {
            const stage = stages.find(s => s.id === automation.stage_id);
            
            return (
              <Card key={index} className="border-border/50">
                <CardHeader className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 flex-1">
                      <Switch
                        checked={automation.is_active}
                        onCheckedChange={(checked) => updateAutomation(index, { is_active: checked })}
                      />
                      <div className="flex-1 cursor-pointer" onClick={() => setExpandedIndex(expandedIndex === index ? null : index)}>
                        <CardTitle className="text-sm font-medium">
                          {automation.action_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </CardTitle>
                        <p className="text-xs text-muted-foreground mt-1">
                          When entering: {stage?.stage_name || 'Unknown Stage'}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeAutomation(index)}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardHeader>
                
                {expandedIndex === index && (
                  <CardContent className="p-4 pt-0 space-y-3 border-t">
                    <div className="space-y-1">
                      <Label className="text-xs">Trigger Stage</Label>
                      <Select
                        value={automation.stage_id}
                        onValueChange={(value) => updateAutomation(index, { stage_id: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {stages.map((stage) => (
                            <SelectItem key={stage.id || stage.stage_key} value={stage.id || stage.stage_key}>
                              {stage.stage_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-1">
                      <Label className="text-xs">Action Type</Label>
                      <Select
                        value={automation.action_type}
                        onValueChange={(value: any) => updateAutomation(index, { 
                          action_type: value,
                          action_config: {} // Reset config when changing type
                        })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="create_task">Create Task</SelectItem>
                          <SelectItem value="send_email">Send Email</SelectItem>
                          <SelectItem value="send_notification">Send Notification</SelectItem>
                          <SelectItem value="webhook">Call Webhook</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {renderActionConfig(automation, index)}
                  </CardContent>
                )}
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
};
